import { type BaseErrorOptions } from '@canvas/error';
import { isAnonymous } from '@canvas/frame-api/is-anonymous';
import { safeGet, safeSet } from '@canvas/idb-cache';
import {
  type IdentityOld,
  type CanvasIdentity,
  type CanvasIdentityOld,
  IdentityKind,
  type UserIdentity,
  ANONYMOUS_USER,
} from '@canvas/model/identity';
import {
  RecoverableNetworkError,
  retryWithBackoff,
  UnrecoverableNetworkError,
} from '@canvas/retry';

import { fetchWithMemoryCache } from './network/fetch-with-memory-cache';
import { type AbortableResponse } from './network/fetch-with-timeout';

export class IdentityAuthenticationError extends UnrecoverableNetworkError {
  constructor(message: string, options?: BaseErrorOptions) {
    super(message, undefined, { name: 'IdentityAuthenticationError', ...options });
  }
}

export class IdentityProxyError extends UnrecoverableNetworkError {
  constructor(message: string, options?: BaseErrorOptions) {
    super(message, undefined, { name: 'IdentityProxyError', ...options });
  }
}

export class RecoverableIdentityFetchError extends RecoverableNetworkError {
  constructor(message: string, options?: BaseErrorOptions) {
    super(message, { name: 'RecoverableIdentityFetchError', ...options });
  }
}

const IDENTITY_IDB_KEY = 'identity_v2';

const fetchIdentity = async () => {
  let result: AbortableResponse;
  try {
    result = await fetchWithMemoryCache('/gateway/api/me', {}, { cacheKey: 'identity' });
  } catch (e) {
    throw new RecoverableIdentityFetchError(`Unable to fetch identity: ${e}`);
  }

  if (!result.ok) {
    switch (result.status) {
      case 502: {
        if (!window.location.hostname.endsWith('atlassian.com')) {
          throw new IdentityProxyError(
            `Unable to reach identity from ${window.location.hostname}`
          );
        } else {
          throw new RecoverableNetworkError(
            `Unable to fetch identity (HTTP ${result.status}) but can retry`
          );
        }
      }
      case 401:
        throw new IdentityAuthenticationError(
          `Unable to fetch identity, authentication error`
        );
      case 404: {
        if (!window.location.hostname.endsWith('atlassian.com')) {
          throw new IdentityProxyError(
            `Unable to reach identity from ${window.location.hostname}`
          );
        } else {
          throw new UnrecoverableNetworkError(`Unable to fetch identity.`);
        }
      }
      case 408:
        throw new RecoverableNetworkError(`Unable to reach identity due to timeout.`);

      default:
        if (result.status >= 500) {
          throw new RecoverableNetworkError(
            `Unable to fetch identity (HTTP ${result.status}) but can retry`
          );
        } else {
          throw new UnrecoverableNetworkError(
            `Unable to fetch identity due to unknown problem with status ${result.status}`
          );
        }
    }
  }

  const { picture, account_id, nickname, email, locale } = await result.json();

  return {
    picture,
    accountId: account_id,
    nickname,
    email,
    locale,
    type: IdentityKind.CanvasId,
  } satisfies CanvasIdentity;
};

export const synchroniseIdentity = async () => {
  try {
    // We have to assume that any of these types could've been stored in the cache
    const identityCached = await safeGet<
      IdentityOld | CanvasIdentityOld | CanvasIdentity
    >(IDENTITY_IDB_KEY);

    if (!identityCached) {
      return;
    }

    const identity = await retryWithBackoff({
      callback: () => fetchIdentity(),
      maxRetries: 5,
      retryTimeMs: 500,
      shouldRetry: (error) => error instanceof RecoverableNetworkError,
    });

    if (
      isOldIdentity(identityCached) ||
      identity.nickname !== identityCached.nickname ||
      identity.picture !== identityCached.picture ||
      identity.locale !== identityCached.locale
    ) {
      safeSet(IDENTITY_IDB_KEY, identity);
    }
  } catch (err) {
    console.error('unable to synchronise identity');
  }
};

/**
 * TODO: Split to an implementation specific to Whiteboards: We will eventually split detail to enable
 * fetchIdentity() to return anonymous users
 * @returns Identity details of an authenticated or anonymous user
 */
export const getWhiteboardsIdentity = async (): Promise<UserIdentity> => {
  // @TODO: This check should be moved once we have a proper way to query if the user is anonymous
  // and placed alongside it
  if (isAnonymous()) {
    return ANONYMOUS_USER;
  }

  return await getIdentity();
};

/**
 * Get the current user from the Identity API (or cached in IDB)
 */
export const getIdentity = async (): Promise<CanvasIdentity> => {
  // We have to assume that any of these types could've been stored in the cache and deal with
  // them accordingly
  const identityCached = await safeGet<IdentityOld | CanvasIdentityOld | CanvasIdentity>(
    IDENTITY_IDB_KEY
  );

  if (identityCached) {
    const accountId: string = isOldIdentity(identityCached)
      ? identityCached.account_id
      : identityCached.accountId;
    return { ...identityCached, accountId, type: IdentityKind.CanvasId };
  }

  // Otherwise, we need to re-query for the identity and store it
  const identity = await retryWithBackoff({
    callback: () => fetchIdentity(),
    maxRetries: 5,
    retryTimeMs: 500,
    shouldRetry: (error) => error instanceof RecoverableNetworkError,
  });

  safeSet(IDENTITY_IDB_KEY, identity);

  return identity;
};

export const setIdentity = (identity: CanvasIdentity) => {
  safeSet(IDENTITY_IDB_KEY, identity);
};

const isOldIdentity = (
  identity: IdentityOld | CanvasIdentityOld | CanvasIdentity
): identity is IdentityOld => {
  return 'account_id' in identity;
};
